import type {
	HomeScheduleItem,
	HomeUpcomingScheduleItem,
} from "@/api/adapters/home-dashboard";

import { EmptyMessage } from "@/components/feedback/EmptyMessage";
import { RiskPill } from "@/components/ui/RiskPill";
import { SCH_TYPE_BADGE } from "../constants";
import { formatSchDate, maskNameKeepLast } from "../utils";

interface Props {
	today: HomeScheduleItem[];
	upcoming: HomeUpcomingScheduleItem[];
	todayStr: string;
	t: (key: string, opts?: Record<string, unknown>) => string;
	navigate: (path: string) => void;
}

export function ScheduleDetail({
	today,
	upcoming,
	todayStr,
	t,
	navigate,
}: Props) {
	const schRow = (
		s: HomeScheduleItem | HomeUpcomingScheduleItem,
		withDate: boolean,
	) => {
		const datePart =
			withDate && "date" in s
				? `${formatSchDate((s as HomeUpcomingScheduleItem).date)} `
				: "";
		return (
			<div key={s.id} className="modal-pat modal-pat--schedule">
				<div className="modal-pat__main">
					<div className="modal-pat__head">
						<a
							href={`#/patients?selected=${s.uid}&tab=intervention`}
							className="modal-pat__name"
							onClick={(e) => {
								e.preventDefault();
								navigate(`/patients?selected=${s.uid}&tab=intervention`);
							}}
						>
							{datePart}
							<strong>{s.time}</strong>
							<span
								className={`badge ${SCH_TYPE_BADGE[s.type] || "badge--gray"}`}
							>
								{s.typeLabel}
							</span>
							<strong>{maskNameKeepLast(s.patientName)}</strong>
							{s.mrn && <small>({s.mrn})</small>}
							{" · "}
							{s.primaryDxName}
							{/* SHCT-828 — 의료진웹 PRD 11 v1.18 「R값 칩 canonical」 names all four home
							    detail panels, 중재 일정 included. This row kept the old
							    grade-chip--r, which renders the score with no 위험도 label and in a
							    colour that does not follow the level. */}
							<RiskPill riskScore={s.riskScore} riskLevel={s.riskLevel} t={t} />
						</a>
					</div>
					<div className="modal-pat__detail">
						{s.purpose}
						{s.memo ? ` · 📝 ${s.memo}` : ""}
						{" · "}
						{t("medical:home.sections.schedule.inCharge", {
							staff: s.assignedStaff,
						})}
					</div>
				</div>
				<div className="modal-pat__actions">
					<a
						href={`#/patients?selected=${s.uid}&tab=intervention`}
						className="btn btn--xs btn--secondary"
						onClick={(e) => {
							e.preventDefault();
							navigate(`/patients?selected=${s.uid}&tab=intervention`);
						}}
					>
						{t("medical:home.detail.goToPatient")}
					</a>
				</div>
			</div>
		);
	};

	return (
		<>
			<div className="sched-group">
				<div className="sched-group__label">
					<span>
						{t("medical:home.sections.schedule.todayHeader", {
							date: todayStr,
						})}
					</span>
					<span className="sched-group__count">
						{today.length}
						{t("medical:home.sections.schedule.unit")}
					</span>
				</div>
				{today.length ? (
					<div className="modal-pat-list">
						{today.map((s) => schRow(s, false))}
					</div>
				) : (
					<EmptyMessage msg={t("medical:home.sections.schedule.todayEmpty")} />
				)}
			</div>
			<div className="sched-group">
				<div className="sched-group__label">
					<span>{t("medical:home.sections.schedule.upcomingHeader")}</span>
					<span className="sched-group__count">
						{upcoming.length}
						{t("medical:home.sections.schedule.unit")}
					</span>
				</div>
				{upcoming.length ? (
					<div className="modal-pat-list">
						{upcoming.map((s) => schRow(s, true))}
					</div>
				) : (
					<EmptyMessage
						msg={t("medical:home.sections.schedule.upcomingEmpty")}
					/>
				)}
			</div>
		</>
	);
}
